<template>
    <div class="lni-c-janitorial-job-plan">
        <div
            v-show="isLoading"
            class="lni-u-text--center"
        >
            <lni-spinner
                :id="`${id}_page-spinner`"
            />
        </div>
        <h2 class="lni-u-heading--2">
            {{ content?.jobPlanHeading }}
        </h2>
        <div class="lni-u-form-width--base lni-u-mt2">
            <lni-input-text
                :id="`${id}_name`"
                class="lni-u-mt0"
                :labelText="content?.jobPlanNameLabel"
                :fullWidth="true"
                :required="true"
                :value="name"
                :hasInlineMessages="false"
            />
        </div>
        <div class="lni-u-mt3 lni-u-reading-width--max">
            <h3 class="lni-u-heading--3">
                {{ content?.activityHeading }}
            </h3>
            <lni-select
                :id="`${id}_task`"
                :labelText="content?.taskLabel"
                class="lni-u-mt2 lni-u-full-width"
                :required="true"
                :hasInlineMessages="false"
                @change="getNextCategory('task', 'location', ['location', 'tool', 'variation'])"
            />
            <lni-collapse
                :id="`${id}_location-collapse`"
                class="lni-u-mt2"
                :visible="false"
            >
                <lni-select
                    :id="`${id}_location`"
                    :labelText="content?.locationLabel"
                    class="lni-u-full-width"
                    :required="true"
                    :hasInlineMessages="false"
                    @change="getNextCategory('location', 'tool', ['tool', 'variation'])"
                />
            </lni-collapse>
            <lni-collapse
                :id="`${id}_tool-collapse`"
                class="lni-u-mt2"
                :visible="false"
            >
                <lni-select
                    :id="`${id}_tool`"
                    :labelText="content?.toolLabel"
                    class="lni-u-full-width"
                    :required="true"
                    :hasInlineMessages="false"
                    @change="getNextCategory('tool', 'variation', ['variation'])"
                />
            </lni-collapse>
            <lni-collapse
                :id="`${id}_variation-collapse`"
                class="lni-u-mt2"
                :visible="false"
            >
                <lni-select
                    :id="`${id}_variation`"
                    :labelText="content?.variationLabel"
                    class="lni-u-full-width"
                    :required="true"
                    :hasInlineMessages="false"
                    @change="onChange"
                />
            </lni-collapse>
            <div
                v-if="sectionLoading"
                class="lni-u-text--center lni-u-full-height"
            >
                <lni-spinner
                    :id="`${id}_section-spinner`"
                    :inline="true"
                />
            </div>
        </div>
        <div class="lni-u-mt3 lni-u-form-width--base">
            <h3 class="lni-u-heading--3">
                {{ content?.measurementHeading }}
            </h3>
            <div class="lni-u-mt2 --row lni-u-gap2">
                <lni-select
                    :id="`${id}_hours`"
                    :labelText="content?.hoursLabel"
                    :required="true"
                    :options="hourOptions"
                    :hasInlineMessages="false"
                    :changeAction="`${id}/timeCustomValidation`"
                />
                <lni-select
                    :id="`${id}_minutes`"
                    :labelText="content?.minutesLabel"
                    :required="true"
                    :options="minuteOptions"
                    :hasInlineMessages="false"
                    :changeAction="`${id}/timeCustomValidation`"
                />
            </div>
            <div class="lni-u-flex lni-u-gap2 lni-u-mt2 lni-u-pt1">
                <lni-input-text
                    :id="`${id}_quantity`"
                    class="lni-u-mt0"
                    :labelText="content?.quantityLabel"
                    type="number"
                    :required="true"
                    min="0"
                    customWidth="12"
                    :hasInlineMessages="false"
                    :changeAction="`${id}/quantityCustomValidation`"
                />
                <div class="lni-u-mvauto">
                    <span class="lni-u-type--bold">
                        {{ measurementText }}
                    </span>
                </div>
            </div>
        </div>
        <div class="lni-u-mt3 lni-u-full-width lni-u-flex lni-u-flex-wrap">
            <div class="lni-u-flex lni-u-mr3">
                <lni-button
                    :id="`${id}_save`"
                    class="lni-u-mr1"
                    :text="content?.saveLabel"
                    :errorContainerId="`${id}_errors`"
                    :clickAction="['validateForm', `${id}/updateJobPlan`]"
                />
                <!-- TODO: Fix edit > don't save and generate > go back to edit and button is still visible -->
                <button
                    v-if="editIndex !== null"
                    :id="`${id}_cancel`"
                    class="lni-c-button --alternate lni-u-text--all-caps lni-u-ml1"
                    @click="clear"
                >
                    {{ `${content?.editCancelLabel} #${editIndex + 1}` }}
                </button>
            </div>
            <lni-collapse
                :id="`${id}_notification-collapse`"
                :visible="false"
            >
                <lni-notification
                    :id="`${id}_notification`"
                    class="lni-u-type--xs lni-u-line-height--tight lni-u-pv1"
                    type="success"
                >
                    <span
                        class="lnicon--checkmark"
                        aria-hidden="true"
                    />
                    {{ content?.savedMessage }}
                </lni-notification>
            </lni-collapse>
        </div>
        <div class="lni-u-reading-width--max">
            <lni-error-container
                :id="`${id}_errors`"
                class="lni-u-mt3"
                :errorsCollectedFrom="trackedErrors"
            />
        </div>
        <div
            v-show="jobPlan?.length"
            class="lni-u-mt3"
        >
            <div class="lni-u-pt1 lni-u-bt lni-u-b--subtle">
                <lni-janitorial-job-plan-table
                    :id="`${id}_job-plan-table`"
                    :items="jobPlan"
                    @edit="editItem"
                    @remove="displayModal"
                />
                <button
                    :id="`${id}_generate`"
                    class="lni-c-button --caret lni-u-text--all-caps lni-u-mt2"
                    href="#"
                    @click="onClick"
                >
                    {{ buttonText }}
                </button>
                <div class="lni-u-reading-width--max">
                    <lni-error-container
                        :id="`${id}_error-container`"
                        class="lni-u-mt3"
                        :errorsCollectedFrom="[
                            `${id}_name`,
                        ]"
                    />
                </div>
            </div>
        </div>
        <lni-modal
            :id="`${id}_modal`"
            :title="content?.removeTitle"
            :allowClose="true"
            :compact="true"
            :visible="false"
        >
            <template slot="content">
                <div class="lni-c-janitorial-workload-calculator lni-u-flex lni-u-flex-wrap">
                    <div class="lni-u-flex">
                        <span
                            class="lnicon--warning lni-u-type--xxl lni-u-mvauto lni-u-pr2"
                            aria-hidden="true"
                        />
                        {{ content?.removeMessage }}
                    </div>
                </div>
                <div class="lni-u-mt2 lni-u-flex lni-u-justify-end">
                    <lni-button
                        :id="`${id}_modal-yes`"
                        class="lni-u-mr2"
                        :text="content?.yesLabel"
                        @click="removeItem"
                    />
                    <lni-button
                        :id="`${id}_modal-cancel`"
                        class="--alternate"
                        :text="content?.cancelLabel"
                        @click="closeModal"
                    />
                </div>
            </template>
        </lni-modal>
    </div>
</template>

<script>
import {
    mapGetters,
} from 'vuex';

import Types from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/enums/types';

export default {
    'name': 'LniJanitorialJobPlan',
    data() {
        return {
            removeIndex: null,
        };
    },
    computed: {
        buttonText() {
            let result = `${this.content.generateText} `;
            if (this.type === Types.Job) {
                result += this.content.jobReportText;
            } else if (this.type === Types.Management) {
                result += this.content.managementReportText;
            } else if (this.type === Types.Ergonomic) {
                result += this.content.ergonomicEvaluationText;
            }

            return result;
        },
        hourOptions() {
            const result = [];
            for (let i = 0; i <= 12; i++) {
                result.push({
                    text: `${i}`,
                    value: `${i}`,
                });
            }
            return result;
        },
        minuteOptions() {
            const result = [];
            for (let i = 0; i < 60; i += 5) {
                result.push({
                    text: `${i}`,
                    value: `${i}`,
                });
            }
            return result;
        },
        ...mapGetters({
            content: 'appState/content',
            editIndex: 'appState/editIndex',
            isLoading: 'appState/isLoading',
            jobPlan: 'appState/jobPlan',
            measurementText: 'appState/measurementText',
            name: 'appState/name',
            type: 'appState/type',
        }),
    },
    mounted() {
        this.$store.dispatch(`${this.id}/initialize`, {
            oneLni: this.$oneLni,
            sourceId: this.id,
        }, {
            root: true,
        }).then(() => {
            this.$store.commit('appState/SET_VALUE', {
                key: 'isLoading',
                value: false,
            }, {
                root: true,
            });
        });

        const hoursLabel = this.$store.state[`${this.id}_hours`].labelText;
        const minutesLabel = this.$store.state[`${this.id}_minutes`].labelText;
        this.$store.commit(`${this.id}_hours/addValidityTest`, {
            validityTest: 'invalidTime',
            inline: () => `"${hoursLabel}" and "${minutesLabel}" cannot both be 0.`,
            global: () => `"${hoursLabel}" and "${minutesLabel}" cannot both be 0. Please select a valid time amount.`,
        });
        this.$store.commit(`${this.id}_minutes/addValidityTest`, {
            validityTest: 'invalidTime',
            inline: () => `"${hoursLabel}" and "${minutesLabel}" cannot both be 0.`,
            global: () => `"${hoursLabel}" and "${minutesLabel}" cannot both be 0. Please select a valid time amount.`,
        });

        this.$store.commit(`${this.id}_quantity/addValidityTest`, {
            validityTest: 'lessThanMin',
            inline: state => `Quantity must be greater than ${state.min}.`,
            global: state => `Quantity must be greater than ${state.min}.`,
        });

        this.$store.commit(`${this.id}/ADD_TRACKED_ERRORS`, [
            `${this.id}_task`,
            `${this.id}_hours`,
            `${this.id}_minutes`,
            `${this.id}_quantity`,
        ]);

        this.$store.commit('setAttribute', {
            id: `${this.id}_errors`,
            attribute: 'errorsCollectedFrom',
            value: this.trackedErrors,
        }, {
            root: true,
        });
    },
    methods: {
        clear() {
            this.$store.dispatch(`${this.id}/clear`, { oneLni: this.$oneLni }, { root: true });
        },
        closeModal() {
            this.removeIndex = null;
            this.$store.commit('setAttribute', {
                id: `${this.id}_modal`,
                attribute: 'visible',
                value: false,
            }, {
                root: true,
            });
        },
        displayModal(index) {
            this.removeIndex = index;
            this.$store.commit('setAttribute', {
                id: `${this.id}_modal`,
                attribute: 'visible',
                value: true,
            }, {
                root: true,
            });
        },
        editItem(index) {
            this.$store.dispatch(`${this.id}/editItem`, {
                oneLni: this.$oneLni,
                sourceId: this.id,
                index,
            }, {
                root: true,
            });
        },
        async getNextCategory(target, next, categories) {
            this.$store.commit('setAttribute', {
                id: this.id,
                attribute: 'sectionLoading',
                value: true,
            }, {
                root: true,
            });

            await this.$store.dispatch(`${this.id}/reset`, {
                oneLni: this.$oneLni,
                customData: {
                    categories,
                },
            }, {
                root: true
            });

            await this.$store.dispatch(`${this.id}/onChange`, {
                oneLni: this.$oneLni,
                sourceId: this.id,
                targetId: `${this.id}_${target}`,
            }, {
                root: true,
            });

            const nextId = `${this.id}_${next}`;
            const nextState = this.$store.state[nextId];
            this.$store.commit('setAttribute', {
                id: nextId,
                attribute: 'options',
                value: this.$store.state.appState.data[`${next}s`], // Plural
            }, {
                root: true,
            });
            this.$store.commit('setAttribute', {
                id: `${nextId}-collapse`,
                attribute: 'visible',
                value: true,
            }, {
                root: true,
            });

            if (nextState.required) {
                this.$store.commit(`${this.id}/ADD_TRACKED_ERRORS`, [nextId]);
            }

            this.$store.commit('setAttribute', {
                id: this.id,
                attribute: 'sectionLoading',
                value: false,
            }, {
                root: false,
            });
        },
        onChange() {
            this.$store.dispatch(`${this.id}/onChange`, {
                oneLni: this.$oneLni,
                sourceId: this.id,
                targetId: `${this.id}_variation`,
            }, {
                root: true,
            });
        },
        async onClick() {
            const errorsId = `${this.id}_error-container`;
            const payload = {
                targetId: errorsId,
                validation: {
                    errorsCollectedBy: null,
                },
            };

            await this.$store.dispatch('validateForm', payload);
            const errors = this.$store.state[errorsId].errors;
            if (!errors || errors.length === 0) {
                this.$store.dispatch(`${this.id}/generateReport`, {
                    router: this.$router,
                });
            }
        },
        removeItem() {
            this.$store.commit('appState/REMOVE_FROM_JOB_PLAN', this.removeIndex, { root: true });
            if (this.$store.state.appState.editIndex === this.removeIndex) {
                this.$store.dispatch(`${this.id}/clear`, { oneLni: this.$oneLni }, { root: true });
            }
            this.$store.dispatch(`${this.id}_job-plan-table/updateTable`, {
                oneLni: this.$oneLni,
            }, {
                root: true,
            });
            this.closeModal();
        },
    },
};
</script>